import { useState } from 'react';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns the Payment modal's own UI state
// (open/closed, the deposit/charge input fields) plus its add/delete
// handlers and the derived stats (lesson counts, totals) it displays.
// Actual balance math (the reconciliation, the Supabase write) stays in
// usePaymentBalance — this is just the modal's own layer on top of it.
export function usePaymentModalActions({ payments, savePayments, priceAmount, paymentBalance }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDepositInput, setPaymentDepositInput] = useState('');
  const [paymentDepositNote, setPaymentDepositNote] = useState('');
  const [paymentChargeInput, setPaymentChargeInput] = useState('');
  const [paymentChargeNote, setPaymentChargeNote] = useState('');

  const addPaymentDeposit = () => {
    const amt = parseFloat(paymentDepositInput);
    if (!amt || amt <= 0) return;
    const next = [...payments, {
      id: Date.now(),
      date: new Date().toISOString(),
      amount: amt,
      type: 'deposit',
      note: paymentDepositNote.trim() || 'Payment received',
    }];
    savePayments(next);
    setPaymentDepositInput('');
    setPaymentDepositNote('');
  };

  const addPaymentCharge = () => {
    const amt = parseFloat(paymentChargeInput);
    if (!amt || amt <= 0) return;
    const next = [...payments, {
      id: Date.now(),
      date: new Date().toISOString(),
      amount: -amt,
      type: 'charge',
      note: paymentChargeNote.trim() || 'Lesson charge',
    }];
    savePayments(next);
    setPaymentChargeInput('');
    setPaymentChargeNote('');
  };

  const deletePayment = (id) => {
    savePayments(payments.filter(p => p.id !== id));
  };

  // How many lessons the balance covers (positive = paid ahead, negative = owed)
  const lessonPriceNum = parseFloat(priceAmount) || 0;
  const lessonCount = lessonPriceNum > 0 ? Math.round(Math.abs(paymentBalance) / lessonPriceNum) : 0;
  const lessonCountLabel = lessonCount > 0
    ? paymentBalance > 0
      ? `${lessonCount} lesson${lessonCount !== 1 ? 's' : ''} paid ahead`
      : `${lessonCount} lesson${lessonCount !== 1 ? 's' : ''} unpaid`
    : null;

  // Total deposited vs total charged (for modal stats)
  const totalDeposited = payments.filter(p => p.amount > 0).reduce((s, p) => s + p.amount, 0);
  const totalCharged   = payments.filter(p => p.amount < 0).reduce((s, p) => s + Math.abs(p.amount), 0);
  const paidLessons    = lessonPriceNum > 0 ? Math.floor(totalDeposited / lessonPriceNum) : null;
  const chargedLessons = lessonPriceNum > 0 ? Math.round(totalCharged   / lessonPriceNum) : null;

  return {
    showPaymentModal, setShowPaymentModal,
    paymentDepositInput, setPaymentDepositInput,
    paymentDepositNote, setPaymentDepositNote,
    paymentChargeInput, setPaymentChargeInput,
    paymentChargeNote, setPaymentChargeNote,
    addPaymentDeposit,
    addPaymentCharge,
    deletePayment,
    lessonCountLabel,
    paidLessons,
    chargedLessons,
  };
}
